<script setup lang="ts">
import { storeToRefs } from 'pinia';

import FrogLoading from '@clovyr/bed/assets/images/frog_loading.webp';

import { useEventBus } from '@/stores/event_bus';
import {
  totalUpdateSteps,
  type UpdateJob,
  UpdateStatus,
  useUpdateStore,
} from '@/stores/update_store';

import Module from '../../components/elements/Module.vue';
import { useAppStatus } from '../../composables/useAppStatus';
import { useAppVersion } from '../../composables/useAppVersion';
import { useLaunchStore } from '../../stores/launch_store';

import ProgressBar from './ProgressBar.vue';

interface LibraryAppDetail {
  id: string;
}
const props = defineProps<LibraryAppDetail>();
const deploymentID = toRef(props, 'id');

const { eventBus } = useEventBus();

const {
  deployment,
  isClovyrCode,
  isLaunchFailed,
  isLaunching,
  isRunning,
  isSleeping,
  isWaking,
  isUpdating,
  isRestoring,
  isExpired,
  isAppSubscribed,
} = useAppStatus(deploymentID);
const { isUpdateAvailable } = useAppVersion(deploymentID);

const launchStore = useLaunchStore();
const launch = computed(() => {
  const candidate = launchStore.getLaunchForDeployment(deployment.value.id);
  return candidate?.deployment.libraryItemID === deployment.value.libraryItemID
    ? candidate
    : undefined;
});
const launchTasks = computed(() => launch.value?.tasks || []);
const currentTaskIndex = computed(() => launch.value?.currentTaskIndex);
const currentTask = computed(() => launchTasks.value[currentTaskIndex.value!]);
const hasStalledLaunch = computed(() => isLaunching.value && !launch.value);

const { updateJobs } = storeToRefs(useUpdateStore());
const updateJob = computed(() => {
  const candidate = updateJobs.value[deployment.value.id];
  return candidate?.deployment.libraryItemID === deployment.value.libraryItemID
    ? candidate
    : undefined;
});
const initialProgress = ref(0);
const maxProgress = ref(25);

const statusMap = {
  [UpdateStatus.Started]: 'Starting update...',
  [UpdateStatus.Pulling]: 'Pulling images...',
  [UpdateStatus.Stopping]: 'Stopping services...',
  [UpdateStatus.Snapshotting]: 'Backing up...',
  [UpdateStatus.Restarting]: 'Restarting services...',
  [UpdateStatus.Complete]: 'Done!',
};

const statusToText = (job: UpdateJob | undefined) => {
  if (!job) {
    return '';
  }
  return statusMap[job.status];
};

const AppStatus = computed(() => {
  const closeWarning = `Please do not close your browser tab or window during this process, or ${deployment.value.appName} will not launch`;

  let hed: string;
  let title: string;
  let image: string;
  let subtext: string;

  // Failed
  if (isLaunchFailed.value) {
    hed = 'Error';
    title = `${deployment.value.appName} failed to launch`;
    subtext = 'Please try again or contact Clovyr support for help';
    return { hed, title, subtext };
  }

  if (isLaunching.value || isWaking.value) {
    title = '';
    if (launchTasks.value.length > 0) {
      title = currentTask.value?.description ?? 'Preparing to launch...';
    }
    hed = `Launching ${deployment.value.appName}...`;
    image = FrogLoading;
    subtext = closeWarning;
    return { hed, image, subtext, title };
  }

  // Updating
  if (isUpdating.value) {
    hed = 'Updating...';
    image = FrogLoading;
    subtext = closeWarning;
    title = statusToText(updateJob.value);
    return { hed, image, subtext, title };
  }

  // Expired
  if (isExpired.value) {
    hed = 'Paused';
    title = `${deployment.value.appName} is paused, but all data remains intact on this device.`;
    subtext = `Your hosting trial has expired. ${deployment.value.appName} and its data will be deleted in 30 days, unless you upgrade your hosting now.`;
    return { hed, title, subtext };
  }

  // Running, app update available
  if (isUpdateAvailable.value && isRunning.value) {
    hed = 'Success!';
    title = `${deployment.value.appName} is up and running!`;
    subtext = `There is an update available for ${deployment.value.appName}. Do you want to update?`;
    return { hed, title, subtext, allowUpdate: true };
  }

  // Restoring backup
  if (isRestoring.value) {
    hed = 'Restoring from backup...';
    subtext = closeWarning;
    return { hed, subtext };
  }

  // Sleeping
  if (isSleeping.value) {
    const open = isClovyrCode.value || !isAppSubscribed.value ? 'Open' : 'Resume';
    hed = 'Sleeping';
    title = `${deployment.value.appName} is paused, but all data remains intact. Click "${open}" to run the app.`;
    return { hed, title };
  }

  // Waking
  if (isWaking.value) {
    hed = 'Waking up...';
    image = FrogLoading;
    subtext = `${deployment.value.appName} is getting ready to use.`;
    return { hed, subtext, image };
  }

  // Running
  if (isRunning.value && !isExpired.value) {
    hed = 'Success!';
    title = `${deployment.value.appName} is up to date and running!`;
    return { hed, title };
  }

  return {};
});

watch([AppStatus, launch, updateJob], () => {
  if ((isLaunching.value || isWaking.value) && launchTasks.value.length > 0) {
    const totalTasks = launchTasks.value.length;
    maxProgress.value =
      currentTaskIndex.value === 0 ? 10 : ((currentTaskIndex.value! + 1) / totalTasks) * 100;
  } else if (isUpdating.value && updateJob.value) {
    initialProgress.value = (updateJob.value.status.valueOf() / totalUpdateSteps) * 100;
  }
});

const classNames = computed(() => {
  return [
    isLaunching.value && 'launching',
    isRunning.value && 'success',
    isSleeping.value && 'sleeping',
    isUpdateAvailable.value && 'success-update',
    isUpdating.value && 'updating',
    isRestoring.value && 'restoring',
    isExpired.value && 'suspended',
  ];
});

const onClickUpdate = async (): Promise<void> => {
  eventBus.emit('app:update', { appID: deploymentID.value });
};

onMounted(() => {
  if (hasStalledLaunch.value) {
    eventBus.emit('modal:confirm:show', {
      title: 'Resume Launch?',
      text: `${deployment.value.appName} appears to have stopped launching before it finished. Do you want to resume the launch?`,
      okLabel: 'Resume',
      cancelLabel: 'Not now',
      resolve: (confirmed) => {
        if (!confirmed) {
          return;
        }

        void launchStore.resumeLaunch(deploymentID.value).catch((error) => {
          console.error('failed to resume launch:', error);
          eventBus.emit('toast:show', {
            message: 'Failed to resume the launch. Please try again or contact Clovyr support.',
            type: 'error',
            duration: 5000,
          });
        });
      },
    });
  }
});
</script>

<template>
  <div class="container">
    <!--App Status-->
    <div class="app-instance__twoup-modules row">
      <div class="app-status grid__col-md-12">
        <Module
          class="module--app-status"
          :bigHead="true"
          :hed="AppStatus?.hed"
          :img="AppStatus?.image"
          :class="classNames"
        >
          <template #option>
            <div class="launching-instance" v-if="isLaunching || isWaking || isUpdating">
              <ProgressBar
                v-if="isLaunching || isUpdating"
                :initialProgress="initialProgress"
                :maxProgress="maxProgress"
              />

              <div class="subtext">{{ AppStatus?.subtext }}</div>
            </div>
            <div class="launching-instance" v-else-if="isLaunchFailed">
              <!-- <div class="subtext">Launch Failed</div> -->
            </div>
          </template>
          <template #heading>
            <div class="module__status title">
              {{ AppStatus?.title }}
            </div>
            <span
              class="module__status"
              :class="`module__status--${deployment.state} status-color`"
            ></span>
          </template>
          <template #default v-if="AppStatus?.subtext && !isLaunching && !isWaking && !isUpdating">
            <!-- UPDATE widget -->
            <div class="row" v-if="AppStatus.allowUpdate">
              <div>
                {{ AppStatus?.subtext }}
              </div>
              <div>
                <Button :reversed="true" label="update" @click="onClickUpdate" />
              </div>
            </div>
          </template>
        </Module>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/layout/grid-utils';
@use 'styles/core/layout/grid-vars';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.container {
  padding-top: grid-vars.space(3);
}

.module--app-status {
  :deep(.module__head__grid) {
    display: flex;
    flex-direction: column;

    @include breakpoints.media-breakpoint-up(sm) {
      flex-direction: row;
    }
  }
  :deep(.module__head__left) {
    display: flex;
  }
}
:deep(.module__head__right) {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;

  @include breakpoints.media-breakpoint-up(sm) {
    justify-content: flex-end;
  }
}

:deep(.image-media) {
  margin-right: grid-vars.space(3);
}

.app-status {
  .title {
    color: #d9d9d9;
    padding-right: 0;
  }

  .status-color {
    transform: translateY(-8px);
  }

  &__button {
    display: flex;
    justify-content: flex-end;

    @include breakpoints.media-breakpoint-down(lg) {
      margin-top: 1rem;
    }
  }

  :deep(.module__body) {
    .row {
      @include breakpoints.media-breakpoint-only(md) {
        display: flex;
        flex-direction: column;

        .subtext {
          width: grid-utils.col-width(12);
        }
        .app-status__button {
          width: grid-utils.col-width(12);
        }
      }
    }
  }
}

.launching,
.updating {
  :deep(.image-media) {
    min-width: 75px;
    min-height: 75px;
  }

  :deep(.module__head__right) {
    position: absolute;
    padding: 0.5rem 2.5rem 0 0;
    justify-content: flex-end;
    transform: translateY(-1.5rem);
    min-height: 2rem;
    max-width: unset;

    @include breakpoints.media-breakpoint-up(lg) {
      padding: 0 3rem 2rem 0;
      transform: unset;
    }
    @include breakpoints.media-breakpoint-up(xl) {
      padding-right: 4rem;
    }
  }

  :deep(.module__head__left) {
    padding-top: 2rem;

    @include breakpoints.media-breakpoint-up(lg) {
      padding-top: unset;
    }
  }

  :deep(.module__head__sub) {
    width: 100%;
  }
}

.launching,
.updating,
.restoring {
  .subtext {
    color: #ada6b7;
  }
}

.launching-instance {
  position: relative;

  .subtext {
    margin-top: grid-vars.space(1);
    color: #d9d9d9;
    padding-top: 20px;
  }
}

.success-trial {
  :deep(.module__body) {
    color: #ada6b7;
    background-color: #221a33;
  }

  .row {
    padding-top: grid-vars.space(1.5);
  }

  .grid__col-md-9 {
    padding-top: grid-vars.space(1);
  }
}

.success-update:not(.updating) {
  :deep(.module__body) {
    color: #ada6b7;
    background-color: #a861fc33;
  }

  .row {
    justify-content: space-between;
    div {
      width: auto;
    }
  }
}

.suspended {
  :deep(.module__body) {
    color: #ada6b7;
    background-color: #e76b6d33;
  }

  .button {
    background-color: #fcb173;
  }

  .row {
    padding-top: grid-vars.space(1.5);
  }

  .grid__col-md-9 {
    padding-top: grid-vars.space(1);
    display: flex;
  }

  .space {
    margin-left: grid-vars.space(0.5);
  }
}

.white {
  color: #ffffff;
}
</style>
